import { useEffect, useState } from "react";
import { useForm, useFieldArray, useWatch, type Control } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDolfinClient, useDolfinAuth } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { recurrenceCadenceValues, type components } from "@dolfin/api-public";
import { enumOptions } from "@/lib/enum-options";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Spinner } from "@/components/ui/spinner";
import { Plus, Repeat2, Trash2 } from "lucide-react";
import { InfoHint } from "@/components/InfoHint";
import { ErrorState, Eyebrow, LoadingState } from "@/components/PageUi";
import { addPeriods } from "@/components/SchedulePage";
import { DatePicker } from "@/components/ui/date-picker";
import { formatCalendarDate, formatCurrency, formatDate } from "@/lib/format";
import { toast } from "sonner";
import { CreateCustomerDialog } from "@/components/CreateCustomerDialog";
import { CreateProductDialog } from "@/components/CreateProductDialog";
import { useInvoiceResources, type TaxRateRow } from "@/hooks/useInvoiceResources";
import { BackLink, PageContainer, PageHeader } from "@/components/PagePrimitives";
import { CADENCE_NOUN } from "@/components/ScheduleShared";
import { SearchCombobox } from "@/components/SearchCombobox";
import { ContractExtractBanner, applyContractTerms } from "@/components/ScheduleContract";
import {
  DiscountFields,
  computeTotals,
  discountPayload,
  discountValueIssue,
  discountExceedsIssues,
  discountTypeSchema,
  discountValueSchema,
  type DiscountType,
} from "@/components/Discount";

type ProductResponse = components["schemas"]["ProductResponse"];
type RecurrenceCadence = components["schemas"]["RecurrenceCadence"];
type ExtractedContractTerms = components["schemas"]["ExtractedContractTerms"];

const CADENCES = enumOptions(recurrenceCadenceValues);


const createScheduleSchema = z.object({
  customerId: z.string().min(1, "Customer is required"),
  currency: z.string().min(1, "Currency is required"),
  memo: z.string().nullable(),
  paymentTermsDays: z
    .number()
    .int("Payment terms must be a whole number of days")
    .min(0, "Payment terms cannot be negative")
    .max(365, "Payment terms cannot exceed 365 days"),
  startDate: z.string().min(1, "Start date is required"),
  cadence: z.string().min(1, "Cadence is required"),
  // null = run until paused/cancelled
  totalOccurrences: z
    .number()
    .int("Occurrences must be a whole number")
    .min(1, "Occurrences must be at least 1")
    .nullable(),
  autoSend: z.boolean(),
  discountType: discountTypeSchema,
  discountValue: discountValueSchema,
  lineItems: z
    .array(
      z.object({
        productId: z.string().min(1, "Product is required"),
        quantity: z.number().min(0.01, "Quantity must be greater than 0"),
        taxRateId: z.string().nullable(),
        // null = bill the product's then-current price each occurrence
        unitPriceOverride: z
          .number()
          .min(0, "Price override must not be negative")
          .nullable(),
        discountType: discountTypeSchema,
        discountValue: discountValueSchema,
        // Display-only: the product name as it appeared on a contract, used to seed
        // the product search box before a real product is matched. Never sent to the API.
        productName: z.string().nullish(),
      }),
    )
    .min(1, "At least one line item is required"),
})
  .superRefine((values, ctx) => {
    const headerIssue = discountValueIssue(values.discountType, values.discountValue);
    if (headerIssue)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["discountValue"], message: headerIssue });

    values.lineItems.forEach((item, index) => {
      const issue = discountValueIssue(item.discountType, item.discountValue);
      if (issue)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["lineItems", index, "discountValue"],
          message: issue,
        });
    });
  });

type CreateScheduleFormValues = z.infer<typeof createScheduleSchema>;

// Minimal form shape ScheduleLineItemRow needs — both the create and edit schedule forms
// satisfy it, so the row is shared (cast `form.control` at the call site).
export type ScheduleLineItemsForm = {
  lineItems: {
    productId: string;
    quantity: number;
    taxRateId: string | null;
    unitPriceOverride: number | null;
    discountType: DiscountType;
    discountValue: number | null;
    /** Display-only contract product name; optional so the edit form satisfies this shape too. */
    productName?: string | null;
  }[];
};

export function ScheduleLineItemRow({
  index,
  control,
  products,
  taxRates,
  getProductPrice,
  currency,
  onRemove,
  onNewProduct,
  onProductPicked,
  dirtyRing,
}: {
  index: number;
  control: Control<ScheduleLineItemsForm>;
  products: ProductResponse[];
  taxRates: TaxRateRow[];
  getProductPrice: (productId: string) => number;
  currency: string;
  onRemove: (index: number) => void;
  onNewProduct: () => void;
  /** Called after a product is chosen via search, so the host can record its name for the trigger label. */
  onProductPicked?: (index: number, hit: { id: string; name: string }) => void;
  /** Edit page passes the DIRTY_RING class; create page leaves it undefined. */
  dirtyRing?: string;
}) {
  const api = useDolfinClient();
  const selectedProductId = useWatch({ control, name: `lineItems.${index}.productId` });
  const productName = useWatch({ control, name: `lineItems.${index}.productName` });
  const quantity = useWatch({ control, name: `lineItems.${index}.quantity` });
  const override = useWatch({ control, name: `lineItems.${index}.unitPriceOverride` });
  const discountType = useWatch({ control, name: `lineItems.${index}.discountType` });
  const discountValue = useWatch({ control, name: `lineItems.${index}.discountValue` });
  const productPrice = getProductPrice(selectedProductId);
  const effectivePrice = override ?? productPrice;
  // The schedule-level discount is apportioned across lines, so it shows once in the per-occurrence summary.
  const linePreview = computeTotals([
    {
      quantity: quantity || 0,
      unitPrice: effectivePrice,
      discountType,
      discountValue,
      taxRatePercent: 0,
    },
  ]);
  const lineGross = linePreview.subTotal;
  const lineDiscount = linePreview.lineDiscountTotal;
  const lineTotal = lineGross - lineDiscount;

  return (
    <div className="space-y-3 rounded-xl border bg-muted/30 p-4">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">
          Line {index + 1}
        </span>
        <div className="flex-1" />
        {lineGross > 0 && (
          <span className="text-sm font-semibold">
            {lineDiscount > 0 && (
              <span className="mr-2 font-normal text-muted-foreground line-through">
                {formatCurrency(lineGross, currency)}
              </span>
            )}
            {formatCurrency(lineTotal, currency)}
          </span>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onRemove(index)}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="size-4" />
          <span className="sr-only">Remove line {index + 1}</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_minmax(0,1.5fr)_minmax(0,0.8fr)_minmax(0,1.1fr)]">
        <FormField
          control={control}
          name={`lineItems.${index}.productId`}
          render={({ field: itemField, fieldState }) => (
            <FormItem className="min-w-0">
              <FormLabel className="text-xs">Product</FormLabel>
              <FormControl>
                <SearchCombobox
                  value={itemField.value}
                  selectedLabel={
                    products.find((p) => p.id === itemField.value)?.name ??
                    productName ??
                    undefined
                  }
                  // When unmatched, seed the search with the contract's product name.
                  seedQuery={!itemField.value && productName ? productName : undefined}
                  placeholder={
                    !itemField.value && productName
                      ? `${productName} (search to match)`
                      : "Select product"
                  }
                  searchPlaceholder="Search products…"
                  emptyText="No products found"
                  search={async (q) => {
                    const { data } = await api.GET("/v1/products/search", {
                      params: { query: { q, limit: 10 } },
                    });
                    return (data ?? []).map((hit) => ({
                      id: hit.id!,
                      name: hit.name,
                      sublabel: hit.description,
                    }));
                  }}
                  onSelect={(id, hit) => {
                    itemField.onChange(id);
                    onProductPicked?.(index, hit);
                  }}
                  createOption={{ label: "New product", onSelect: onNewProduct }}
                  className={fieldState.isDirty && dirtyRing ? dirtyRing : undefined}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`lineItems.${index}.taxRateId`}
          render={({ field: itemField, fieldState }) => (
            <FormItem className="min-w-0">
              <FormLabel className="text-xs">Tax Rate</FormLabel>
              <Select
                onValueChange={(val) => itemField.onChange(val === "__none__" ? null : val)}
                value={itemField.value ?? "__none__"}
              >
                <FormControl>
                  <SelectTrigger
                    className={`w-full min-w-0 ${fieldState.isDirty && dirtyRing ? dirtyRing : ""}`}
                  >
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {taxRates.map((rate) => (
                    <SelectItem key={rate.id} value={rate.id}>
                      <span>
                        {rate.name} ({rate.rate}%)
                      </span>
                      {rate.source === "country" && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          Country rate
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`lineItems.${index}.quantity`}
          render={({ field: itemField, fieldState }) => (
            <FormItem className="min-w-0">
              <FormLabel className="text-xs">Quantity</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="any"
                  min="0.01"
                  {...itemField}
                  className={fieldState.isDirty && dirtyRing ? dirtyRing : undefined}
                  onChange={(e) => itemField.onChange(e.target.valueAsNumber)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`lineItems.${index}.unitPriceOverride`}
          render={({ field: itemField, fieldState }) => (
            <FormItem className="min-w-0">
              <FormLabel className="text-xs">Fixed price</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={
                    selectedProductId && productPrice > 0
                      ? `${productPrice.toFixed(2)} (live)`
                      : "Product price"
                  }
                  value={itemField.value ?? ""}
                  className={fieldState.isDirty && dirtyRing ? dirtyRing : undefined}
                  onChange={(e) =>
                    itemField.onChange(
                      e.target.value === "" ? null : e.target.valueAsNumber,
                    )
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <DiscountFields
        control={control}
        typeName={`lineItems.${index}.discountType`}
        valueName={`lineItems.${index}.discountValue`}
        currency={currency}
        label="Line discount"
        compact
      />

      {selectedProductId && (
        <p className="text-xs text-muted-foreground">
          {override != null ? (
            <>
              Fixed at {formatCurrency(override, currency)} per occurrence - product price
              changes won't affect this contract.
            </>
          ) : (
            <>
              Bills the product's current price ({formatCurrency(productPrice, currency)})
              at each occurrence. Set a fixed price for fixed-price contracts.
            </>
          )}
        </p>
      )}
    </div>
  );
}

export function SchedulePlanSummary({
  startDate,
  cadence,
  totalOccurrences,
  autoSend,
  perOccurrence,
  currency,
}: {
  startDate?: string | null;
  cadence?: string | null;
  totalOccurrences?: number | null;
  autoSend: boolean;
  perOccurrence: number;
  currency: string;
}) {
  if (!startDate || !cadence) return null;
  const cadenceKey = cadence as RecurrenceCadence;
  const noun = CADENCE_NOUN[cadenceKey] ?? "period";
  const start = new Date(`${startDate.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) return null;
  const count = totalOccurrences && totalOccurrences > 0 ? totalOccurrences : null;
  // A count typed far past any real contract (999999 annually) runs off the end of the calendar. Say how
  // many invoices rather than naming an end date that does not exist.
  const projected = count ? addPeriods(start, cadenceKey, count - 1) : null;
  const last = projected && !Number.isNaN(projected.getTime()) ? projected : null;

  return (
    <div className="rounded-xl border bg-card/60 p-4 backdrop-blur-sm">
      <Eyebrow>Schedule</Eyebrow>
      <p className="mt-1 text-sm text-pretty">
        {count === 1 ? (
          <>One invoice on {formatCalendarDate(start)}</>
        ) : (
          <>
            Every {noun} from {formatCalendarDate(start)}
            {last ? (
              <>
                {" "}to {formatCalendarDate(last)}, <span className="tabular-nums">{count}</span>{" "}
                invoices
              </>
            ) : count ? (
              <>
                , <span className="tabular-nums">{count}</span> invoices
              </>
            ) : (
              ", until paused or cancelled"
            )}
          </>
        )}
        {autoSend ? ", each sent to the customer automatically." : ", each left as a draft for review."}
      </p>
      {perOccurrence > 0 ? (
        <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-2 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">Per invoice, ex. tax</dt>
            <dd className="font-semibold tabular-nums">{formatCurrency(perOccurrence, currency)}</dd>
          </div>
          {count ? (
            <div>
              <dt className="flex items-center gap-1 text-xs text-muted-foreground">
                Contract value, ex. tax
                <InfoHint label="More about contract value">
                  Per-invoice amount times the number of invoices. Lines without a fixed price follow
                  the product price, so this moves if prices change.
                </InfoHint>
              </dt>
              <dd className="font-semibold tabular-nums">
                {formatCurrency(perOccurrence * count, currency)}
              </dd>
            </div>
          ) : null}
        </dl>
      ) : null}
    </div>
  );
}

export interface CreateSchedulePageProps {
  onCreated: (scheduleId: string) => void;
  onCancel: () => void;
  /** Terms extracted from an uploaded contract: prefill the form and show the confidence banner. */
  extractedTerms?: ExtractedContractTerms | null;
}

export function CreateSchedulePage({
  onCreated,
  onCancel,
  extractedTerms,
}: CreateSchedulePageProps) {
  const api = useDolfinClient();
  const { organisationId } = useDolfinAuth();
  const resources = useInvoiceResources();
  const { products, currencies, taxRates, getProductPrice } = resources;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Display label + seed text for the searchable customer box (the form only stores the id).
  const [customerLabel, setCustomerLabel] = useState("");
  const [customerSeed, setCustomerSeed] = useState("");

  const form = useForm<CreateScheduleFormValues>({
    resolver: zodResolver(createScheduleSchema),
    defaultValues: {
      customerId: "",
      currency: "",
      memo: "",
      // Overwritten with the organisation's configured default once settings load (see fetchData).
      paymentTermsDays: 30,
      startDate: "",
      cadence: "Monthly",
      totalOccurrences: 12,
      autoSend: false,
      discountType: "None",
      discountValue: null,
      lineItems: [{ productId: "", quantity: 1, taxRateId: null, unitPriceOverride: null, discountType: "None" as const, discountValue: null }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "lineItems",
  });

  const watchedLineItems = useWatch({ control: form.control, name: "lineItems" });
  // Watched, not getValues: nothing else re-renders on a currency switch, so a snapshot kept the old currency's labels.
  const watchedCurrency = useWatch({ control: form.control, name: "currency" });
  const watchedCadence = useWatch({ control: form.control, name: "cadence" });
  const watchedStartDate = useWatch({ control: form.control, name: "startDate" });
  const watchedOccurrences = useWatch({ control: form.control, name: "totalOccurrences" });
  const watchedAutoSend = useWatch({ control: form.control, name: "autoSend" });
  const cadenceNoun = CADENCE_NOUN[(watchedCadence as RecurrenceCadence) ?? "Monthly"];
  const watchedDiscountType = useWatch({ control: form.control, name: "discountType" });
  const watchedDiscountValue = useWatch({ control: form.control, name: "discountValue" });
  // Net of both discount levels, so the headline figure is what will actually be billed. Tax excluded.
  const perOccurrenceTotals = computeTotals(
    (watchedLineItems ?? [])
      .filter((li) => li?.productId)
      .map((li) => ({
        quantity: li.quantity || 0,
        unitPrice: li.unitPriceOverride ?? getProductPrice(li.productId),
        discountType: li.discountType,
        discountValue: li.discountValue,
        taxRatePercent: 0,
      })),
    watchedDiscountType,
    watchedDiscountValue,
  );
  const perOccurrenceSubtotal = perOccurrenceTotals.totalAmount;

  // The organisation's default payment terms (net-30 when unset or unavailable), used to pre-fill the field.
  async function fetchOrgDefaultPaymentTermsDays(): Promise<number> {
    if (!organisationId) return 30;
    const { data } = await api.GET("/v1/organisations/{organisationId}/settings/ar", {
      params: { path: { organisationId } },
    });
    const configured = data?.defaultPaymentTermsDays;
    return configured != null ? Number(configured) : 30;
  }

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const loaded = await resources.fetchAll();
        const orgDefaultTerms = await fetchOrgDefaultPaymentTermsDays();

        if (extractedTerms) {
          const defaults = await applyContractTerms(extractedTerms, {
            api,
            products: loaded.products,
            taxRates: loaded.taxRates,
            defaultCurrency: loaded.defaultCurrency,
          });
          setCustomerLabel(defaults.customerLabel);
          setCustomerSeed(defaults.customerSeedQuery);
          form.reset({
            customerId: defaults.customerId,
            currency: defaults.currency,
            memo: defaults.memo,
            paymentTermsDays: orgDefaultTerms,
            startDate: defaults.startDate,
            cadence: defaults.cadence,
            totalOccurrences: defaults.totalOccurrences,
            autoSend: defaults.autoSend,
            // Contract extraction does not infer discounts, so a schedule seeded from a document
            // starts undiscounted and the user adds one explicitly.
            discountType: "None",
            discountValue: null,
            lineItems: defaults.lineItems,
          });
          return;
        }

        form.reset({
          customerId: "",
          currency: loaded.defaultCurrency ?? "",
          memo: "",
          paymentTermsDays: orgDefaultTerms,
          startDate: "",
          cadence: "Monthly",
          totalOccurrences: 12,
          autoSend: false,
          discountType: "None",
          discountValue: null,
          lineItems: [
            { productId: "", quantity: 1, taxRateId: null, unitPriceOverride: null, discountType: "None" as const, discountValue: null },
          ],
        });
      } catch (e) {
        console.error("Failed to load data:", e);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  async function onSubmit(values: CreateScheduleFormValues) {
    // The schedule's discounts are validated per occurrence by the server, so an over-large one is a
    // 422 on create. Caught here first, against the same per-occurrence figures the summary shows.
    const exceeded = discountExceedsIssues(
      computeTotals(
        values.lineItems.map((li) => ({
          quantity: li.quantity || 0,
          unitPrice: li.unitPriceOverride ?? getProductPrice(li.productId),
          discountType: li.discountType,
          discountValue: li.discountValue,
          taxRatePercent: 0,
        })),
        values.discountType,
        values.discountValue,
      ),
    );
    const firstLineIssue = exceeded.lines.findIndex((issue) => issue !== null);
    if (firstLineIssue !== -1) {
      form.setError(`lineItems.${firstLineIssue}.discountValue`, {
        message: exceeded.lines[firstLineIssue] ?? undefined,
      });
      return;
    }
    if (exceeded.invoice) {
      form.setError("discountValue", { message: exceeded.invoice });
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const { data, error: apiError } = await api.POST("/v1/recurring-invoices", {
        body: {
          customerId: values.customerId,
          currency: values.currency,
          memo: values.memo || null,
          paymentTermsDays: values.paymentTermsDays,
          startDate: `${values.startDate}T00:00:00Z`,
          cadence: values.cadence as RecurrenceCadence,
          totalOccurrences: values.totalOccurrences,
          autoSend: values.autoSend,
          ...discountPayload(values.discountType, values.discountValue),
          lineItems: values.lineItems.map((item) => ({
            productId: item.productId,
            quantity: item.quantity,
            taxRateId: item.taxRateId || null,
            unitPriceOverride: item.unitPriceOverride,
            ...discountPayload(item.discountType, item.discountValue),
          })),
        },
      });

      if (apiError || !data?.id) {
        const message = apiErrorMessage(apiError, "Failed to create recurring invoice.");
        setSaveError(message);
        toast.error(message);
        return;
      }

      onCreated(data.id);
    } catch (e) {
      console.error("Failed to create schedule:", e);
      setSaveError("An unexpected error occurred while creating the recurring invoice.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <PageContainer size="narrow">
        <BackLink onClick={onCancel}>Back to recurring invoices</BackLink>
        <LoadingState label="Loading customers and products..." />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer size="narrow">
        <BackLink onClick={onCancel}>Back to recurring invoices</BackLink>
        <ErrorState message={error} />
      </PageContainer>
    );
  }

  return (
    <PageContainer size="narrow">
      <BackLink onClick={onCancel}>Back to recurring invoices</BackLink>
      <PageHeader
        eyebrow="Receivables"
        title="New recurring invoice"
        subtitle="Bill a customer on a cadence from one template. Changes later apply to future invoices only."
      />

      {extractedTerms && (
        <div className="mb-6">
          <ContractExtractBanner terms={extractedTerms} />
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Customer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="customerId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Customer</FormLabel>
                      <FormControl>
                        <SearchCombobox
                          value={field.value}
                          selectedLabel={customerLabel}
                          seedQuery={customerSeed || undefined}
                          placeholder="Search customers…"
                          searchPlaceholder="Search by name or email…"
                          emptyText="No customers found"
                          search={async (q) => {
                            const { data } = await api.GET("/v1/customers/search", {
                              params: { query: { q, limit: 10 } },
                            });
                            return (data ?? []).map((hit) => ({
                              id: hit.id!,
                              name: hit.name,
                              sublabel: hit.email,
                            }));
                          }}
                          onSelect={(id, hit) => {
                            field.onChange(id);
                            setCustomerLabel(hit.name);
                          }}
                          createOption={{
                            label: "New customer",
                            onSelect: () => resources.setNewCustomerOpen(true),
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Currency</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select currency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {currencies.map((c) => (
                            <SelectItem key={c.id} value={c.code}>
                              {c.code} - {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="memo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Memo</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Appears on every generated invoice..."
                        className="resize-none"
                        rows={3}
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Recurrence
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First invoice</FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value || null}
                          onChange={(v) => field.onChange(v ?? "")}
                          placeholder="Start date"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="cadence"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cadence</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select cadence" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CADENCES.map((c) => (
                            <SelectItem key={c.value} value={c.value}>
                              {c.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="totalOccurrences"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Occurrences</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          placeholder="Until cancelled"
                          value={field.value ?? ""}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === "" ? null : e.target.valueAsNumber,
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Leave occurrences empty to run until paused or cancelled. Invoices fall on
                the start date each {cadenceNoun}; month-end dates are clamped (a contract
                starting 31 Jan bills 28 Feb, then 31 Mar).
              </p>

              <FormField
                control={form.control}
                name="paymentTermsDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1">
                      Payment terms (days)
                      <InfoHint label="More about payment terms">
                        Sets the due date on every generated invoice: this many days after each
                        invoice's issue date.
                      </InfoHint>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        max="365"
                        step="1"
                        value={Number.isFinite(field.value) ? field.value : ""}
                        onChange={(e) => field.onChange(e.target.valueAsNumber)}
                        onBlur={(e) => {
                          field.onBlur();
                          if (e.target.value.trim() === "") field.onChange(0);
                        }}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      Applied to every generated invoice. Defaults to the organisation's default terms;
                      0 means due on receipt.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="autoSend"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>Send automatically</FormLabel>
                      <p className="text-xs text-muted-foreground">
                        On: each invoice is generated and emailed to the customer in one
                        step.
                        <br />
                        Off: invoices are left as drafts for you to review.
                      </p>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">
                Line items
              </CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  append({
                    productId: "",
                    quantity: 1,
                    taxRateId: null,
                    unitPriceOverride: null,
                    discountType: "None",
                    discountValue: null,
                  })
                }
              >
                <Plus className="size-4" />
                Add line
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {fields.length === 0 && (
                <p className="rounded-xl border border-dashed bg-card/30 py-6 text-center text-sm text-muted-foreground">
                  No lines yet. Add a line for each product this contract bills.
                </p>
              )}

              {fields.map((field, index) => (
                <ScheduleLineItemRow
                  key={field.id}
                  index={index}
                  control={form.control as unknown as Control<ScheduleLineItemsForm>}
                  products={products}
                  taxRates={taxRates}
                  getProductPrice={getProductPrice}
                  currency={watchedCurrency}
                  onRemove={remove}
                  onNewProduct={() => resources.setNewProductOpen(true)}
                  onProductPicked={(i, hit) =>
                    form.setValue(`lineItems.${i}.productName`, hit.name)
                  }
                />
              ))}

              {form.formState.errors.lineItems?.root && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.lineItems.root.message}
                </p>
              )}

              <div className="border-t pt-3">
                <DiscountFields
                  control={form.control}
                  typeName="discountType"
                  valueName="discountValue"
                  currency={watchedCurrency}
                  label="Invoice discount"
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  Applied to every generated invoice, after any per-line discounts. Resolved afresh
                  each occurrence, so a percentage keeps tracking the contract as prices move.
                </p>
              </div>

              {perOccurrenceTotals.discountTotal > 0 && (
                <div className="flex items-center justify-end gap-2 text-sm">
                  <span className="text-muted-foreground">Per-occurrence discount:</span>
                  <span className="font-medium">
                    -{formatCurrency(perOccurrenceTotals.discountTotal, watchedCurrency)}
                  </span>
                </div>
              )}

              {perOccurrenceSubtotal > 0 && (
                <div className="flex items-center justify-end gap-2 border-t pt-3 text-sm">
                  <Repeat2 className="size-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">
                    Per-occurrence subtotal (ex. tax):
                  </span>
                  <span className="font-semibold">
                    {formatCurrency(perOccurrenceSubtotal, watchedCurrency)}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          <SchedulePlanSummary
            startDate={watchedStartDate}
            cadence={watchedCadence}
            totalOccurrences={watchedOccurrences}
            autoSend={watchedAutoSend}
            perOccurrence={perOccurrenceSubtotal}
            currency={watchedCurrency}
          />

          {saveError && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4">
              <p className="text-sm text-destructive">{saveError}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
            >
              {saving ? <Spinner className="size-4" /> : <Repeat2 className="size-4" />}
              {saving ? "Creating..." : "Create recurring invoice"}
            </Button>
          </div>
        </form>
      </Form>

      <CreateCustomerDialog
        open={resources.newCustomerOpen}
        onOpenChange={resources.setNewCustomerOpen}
        onCreated={resources.refreshCustomers}
      />
      <CreateProductDialog
        open={resources.newProductOpen}
        onOpenChange={resources.setNewProductOpen}
        onCreated={resources.refreshProducts}
      />
    </PageContainer>
  );
}
